import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import * as api from "../lib/api";

const TRACE_EMOJI = {
  passed:      "🗺️",
  regular:     "🔥",
  want_to_try: "🫦",
};

const CATEGORY_BADGE_CLASS = {
  facile:     "border-emerald-500/30 bg-emerald-500/15 text-emerald-200",
  audacieux:  "border-amber-500/30 bg-amber-500/15 text-amber-200",
  temporaire: "border-red-500/30 bg-red-500/15 text-red-200",
};

const TRACE_BADGE_CLASS_NEUTRAL = "border-white/20 bg-white/5 text-white/80";

export default function CruisingSheet({ open, onOpenChange, onOpenSpotById }) {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [mySpots, setMySpots] = useState([]);
  const [myTraces, setMyTraces] = useState([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, spots, traces] = await Promise.all([
        api.getMyStats(),
        api.getMySpots(),
        api.getMyTraces(),
      ]);
      setStats(s);
      setMySpots(spots || []);
      setMyTraces(traces || []);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger ton cruising");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[420px] bg-[#0f0d0e] text-white border-white/10">
        <SheetHeader>
          <SheetTitle className="text-white">Mon cruising</SheetTitle>
        </SheetHeader>

        <ScrollArea className="mt-4 h-[calc(100vh-80px)] pr-2">
          <div className="space-y-4">

            {/* Progression */}
            <div className="p-3 rounded-lg border border-white/10 bg-white/5">
              <div className="text-sm text-white/70">Progression</div>

              <div className="mt-2">
                <Badge variant="secondary" className="w-full justify-center">
                  Spots créés : {stats?.spots_created ?? "—"}
                </Badge>
              </div>

              {stats && (() => {
                const total = (stats?.traces_regular ?? 0) + (stats?.traces_passed ?? 0) + (stats?.traces_want_to_try ?? 0);
                return (
                  <div className="mt-2 text-xs text-white/50">
                    {total} trace{total > 1 ? "s" : ""} laissée{total > 1 ? "s" : ""} sur tes spots
                  </div>
                );
              })()}
              <div className="mt-2 flex gap-2">
                <Badge variant="outline" className={`text-xs ${TRACE_BADGE_CLASS_NEUTRAL}`}>
                  🔥 {stats?.traces_regular ?? "—"}
                </Badge>
                <Badge variant="outline" className={`text-xs ${TRACE_BADGE_CLASS_NEUTRAL}`}>
                  🗺️ {stats?.traces_passed ?? "—"}
                </Badge>
                <Badge variant="outline" className={`text-xs ${TRACE_BADGE_CLASS_NEUTRAL}`}>
                  🫦 {stats?.traces_want_to_try ?? "—"}
                </Badge>
              </div>
            </div>

            {/* Mes spots */}
            <div className="space-y-2">
              <div className="text-sm font-semibold">Mes spots</div>
              <ScrollArea className="h-48 rounded-lg border border-white/10 bg-white/5">
                <div className="p-2 space-y-2">
                  {!mySpots?.length ? (
                    <div className="text-sm text-white/60 p-2">Aucun spot créé</div>
                  ) : (
                    mySpots.map((s) => (
                      <div key={s.id} className="p-2 rounded-md bg-black/30 border border-white/10">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{s.title}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className={`text-xs capitalize ${CATEGORY_BADGE_CLASS[s.category] || TRACE_BADGE_CLASS_NEUTRAL}`}>
                                {s.category}
                              </Badge>
                              <span className="text-xs text-white/60">• {s.status}</span>
                            </div>
                            <span className="text-xs text-white/50 mt-1">
                              🔥 {s.traces_regular ?? 0} · 🗺️ {s.traces_passed ?? 0} · 🫦 {s.traces_want_to_try ?? 0}
                            </span>
                          </div>
                          <button
                            onClick={() => onOpenSpotById?.(s.id)}
                            className="text-white/40 hover:text-white/90 transition-colors p-1"
                            title="Ouvrir ce spot"
                          >
                            <Search className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Mes traces */}
            <div className="space-y-2">
              <div className="text-sm font-semibold">Mes traces</div>
              <div className="text-xs text-white/50">Tu as tracé {myTraces.length} spot{myTraces.length > 1 ? "s" : ""}</div>
              <ScrollArea className="h-48 rounded-lg border border-white/10 bg-white/5">
                <div className="p-2 space-y-2">
                  {!myTraces?.length ? (
                    <div className="text-sm text-white/60 p-2">Aucune trace</div>
                  ) : (
                    myTraces.map((t) => (
                      <div key={t.spot_id} className="p-2 rounded-md bg-black/30 border border-white/10">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-base">{TRACE_EMOJI[t.trace_type]}</span>
                              <span className="text-sm font-medium truncate">{t.title}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className={`text-xs capitalize ${CATEGORY_BADGE_CLASS[t.category] || TRACE_BADGE_CLASS_NEUTRAL}`}>
                                {t.category}
                              </Badge>
                              <span className="text-xs text-white/60">• {t.status}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => onOpenSpotById?.(t.spot_id)}
                            className="text-white/40 hover:text-white/90 transition-colors p-1"
                            title="Ouvrir ce spot"
                          >
                            <Search className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
